// Cookie and storage consent. Strictly necessary storage (the sign-in cookie, draft backups and
// this choice itself) needs no consent; "preferences" (remembering the look on this
// device) and "statistics" (nothing uses it today — reserved for any future analytics) do.
// The choice is kept in localStorage (`eb.consent`, necessary) and can be changed at any time.
import { create } from "zustand";

export type ConsentCategory = "preferences" | "statistics";
/** Bump when the categories or their meaning change: everyone is asked again. */
export const CONSENT_VERSION = 1;
const KEY = "eb.consent";

export interface ConsentChoice {
  v: number;
  preferences: boolean;
  statistics: boolean;
  at: string;
}

interface ConsentState {
  choice: ConsentChoice | null;
  /** The settings dialog is open. */
  open: boolean;
  decide: (c: Omit<ConsentChoice, "v" | "at">) => void;
  openSettings: () => void;
  closeSettings: () => void;
}

function load(): ConsentChoice | null {
  try {
    const c = JSON.parse(localStorage.getItem(KEY) ?? "null") as ConsentChoice | null;
    return c && c.v === CONSENT_VERSION ? c : null;
  } catch {
    return null;
  }
}

const listeners = new Set<(c: ConsentChoice) => void>();

/** Run `fn` whenever the visitor saves a choice (e.g. to delete data they no longer allow). */
export function onConsentChange(fn: (c: ConsentChoice) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const useConsent = create<ConsentState>((set) => ({
  choice: load(),
  open: false,
  decide: (c) => {
    const choice: ConsentChoice = { v: CONSENT_VERSION, ...c, at: new Date().toISOString() };
    try {
      localStorage.setItem(KEY, JSON.stringify(choice));
    } catch {
      /* storage unavailable: the choice lasts for this visit */
    }
    set({ choice, open: false });
    for (const l of listeners) l(choice);
  },
  openSettings: () => set({ open: true }),
  closeSettings: () => set({ open: false }),
}));

/** Has the visitor allowed `category`? (False until they decide.) */
export function hasConsent(category: ConsentCategory): boolean {
  return useConsent.getState().choice?.[category] === true;
}
